import React, { useState } from "react";

import { queryCache } from "react-query";

import { useGetPropertySettings } from "manager/hooks/api";
import { PORTFOLIO_QUERY_KEYS } from "manager/hooks/api/portfolioQueries";
import {
  useCopyPrescreeningDealSetupFromPortfolio,
  PROPERTY_QUERY_KEYS,
  useResetToPrescreeningPortfolioSettings,
  useResetPropertyVeroGoToPortfolioSettings,
} from "manager/hooks/api/propertyQueries";

import ConfirmationModal from "shared/components/Modals/ConfirmationModal";
import { usePermissions } from "shared/hooks";
import useModal from "shared/hooks/useModal";

import { openVeroGoOverrideDialog } from "../../VeroGoSettings/components/shared";

import PropertySettingsFooter from "./PropertySettingsFooter";

interface PropertySettingsFooterContainerProps {
  propertyId: number;
  propertyName: string;
  portfolioName: string;
  copyPortfolioSettings: (
    variables:
      | number
      | {
          propertyId: number;
          skipInvalidation?: boolean;
          skipSuccessNotification?: boolean;
        },
    options?: any
  ) => void;
  resetPropertySettings: (
    variables:
      | number
      | {
          propertyId: number;
          skipInvalidation?: boolean;
        },
    options?: any
  ) => void;
  isPortfolioSettings: boolean;
  isVeroGoFromPortfolio?: boolean;
  propertyVeroGoActive?: boolean;
}

const PropertySettingsFooterContainer = ({
  propertyId,
  propertyName,
  portfolioName,
  copyPortfolioSettings,
  resetPropertySettings,
  isPortfolioSettings,
  isVeroGoFromPortfolio: passedIsVeroGoFromPortfolio,
  propertyVeroGoActive: passedPropertyVeroGoActive,
}: PropertySettingsFooterContainerProps) => {
  const [isActionLoading, setIsActionLoading] = useState(false);
  const { openModalDialog } = useModal();
  const { isVeroGoEnabled } = usePermissions();
  const { propertySettings } = useGetPropertySettings(propertyId);
  const { copyPrescreeningDealSetupFromPortfolio } =
    useCopyPrescreeningDealSetupFromPortfolio();
  const { resetToPrescreeningPortfolioSettings } =
    useResetToPrescreeningPortfolioSettings();
  const { resetPropertyVeroGoToPortfolioSettings } =
    useResetPropertyVeroGoToPortfolioSettings();
  const propertySettingsVeroGo = propertySettings as any;

  const isVeroGoFromPortfolio =
    passedIsVeroGoFromPortfolio ??
    !!propertySettingsVeroGo?.isVeroGoFromPortfolio;
  const propertyVeroGoActive =
    passedPropertyVeroGoActive ?? !!propertySettingsVeroGo?.veroGoActive;
  const isVeroGoActiveForProperty = isVeroGoEnabled && propertyVeroGoActive;
  const isPrescreeningFromPortfolio =
    propertySettingsVeroGo?.isPrescreeningDealSetupFromPortfolio ?? true;
  const hasPrescreeningSetup = Boolean(
    propertySettingsVeroGo?.prescreeningDealSetupId
  );
  const portfolioVeroGoActive = isVeroGoFromPortfolio
    ? propertyVeroGoActive
    : !propertyVeroGoActive;
  const hasVeroGoMismatch = !isVeroGoFromPortfolio;
  const shouldUseVeroGoReset =
    isVeroGoEnabled && (propertyVeroGoActive || portfolioVeroGoActive);
  const willEnableVeroGoOnReset =
    shouldUseVeroGoReset && !propertyVeroGoActive && portfolioVeroGoActive;

  const mutateWithPromise = (mutateFn: any, arg: any) =>
    new Promise<void>((resolve, reject) => {
      mutateFn(arg, {
        onSuccess: () => resolve(),
        onError: () => reject(),
      });
    });

  const copySettings = (variables: number | any = propertyId) =>
    mutateWithPromise(copyPortfolioSettings, variables);
  const resetSettings = (variables: number | any = propertyId) => {
    if (shouldUseVeroGoReset) {
      return mutateWithPromise(
        resetPropertyVeroGoToPortfolioSettings,
        variables
      );
    }
    return mutateWithPromise(resetPropertySettings, variables);
  };
  const copyPrescreeningSettings = (variables: number | any = propertyId) =>
    mutateWithPromise(copyPrescreeningDealSetupFromPortfolio, variables);
  const resetPrescreeningSettings = (variables: number | any = propertyId) =>
    mutateWithPromise(resetToPrescreeningPortfolioSettings, variables);
  const invalidatePropertyVeroGoViews = () => {
    queryCache.invalidateQueries(
      PROPERTY_QUERY_KEYS.getPropertySettings(propertyId)
    );
    queryCache.invalidateQueries(PROPERTY_QUERY_KEYS.getProperties());
    queryCache.invalidateQueries(PORTFOLIO_QUERY_KEYS.getVeroGoSettingsData());
  };

  const openCustomizeSettingsModal = () => {
    const veroGoDescription = isVeroGoActiveForProperty ? (
      <>
        <strong>Warning:</strong> With Vero Go enabled, updating screening
        settings here will also decouple pre-screening settings for this
        property.
      </>
    ) : undefined;

    return openModalDialog(ConfirmationModal, {
      title: "You're going to update property settings",
      subtitle: (
        <span>
          Property: <strong>{propertyName}</strong>
        </span>
      ),
      submitButtonLabel: "Confirm & Continue",
      message: (
        <>
          <strong>Please note:</strong> Once you click &quot;Confirm and
          Continue&quot; new screening and prescreening settings will be created
          for this specific property and the portfolio setup will no longer
          apply.
        </>
      ),
      description: veroGoDescription,
      onConfirm: async () => {
        setIsActionLoading(true);
        try {
          await copySettings();
          if (
            isVeroGoActiveForProperty &&
            isPrescreeningFromPortfolio &&
            hasPrescreeningSetup
          ) {
            await copyPrescreeningSettings();
          }
        } finally {
          setIsActionLoading(false);
        }
      },
    });
  };

  const openResetSettingsModal = () => {
    const performReset = async () => {
      setIsActionLoading(true);
      try {
        await resetSettings();
        if (!shouldUseVeroGoReset && !isPrescreeningFromPortfolio) {
          await resetPrescreeningSettings();
        }
        if (shouldUseVeroGoReset && hasVeroGoMismatch) {
          invalidatePropertyVeroGoViews();
        }
      } finally {
        setIsActionLoading(false);
      }
    };

    if (shouldUseVeroGoReset && hasVeroGoMismatch) {
      return openVeroGoOverrideDialog(openModalDialog, {
        title: "Reset to portfolio setting?",
        subtitle: `${propertyName} in ${portfolioName}`,
        nowState: propertyVeroGoActive ? "on" : "off",
        nowRelationship: "custom",
        afterState: portfolioVeroGoActive ? "on" : "off",
        afterRelationship: "inherited",
        infoText: (
          <>
            This will <strong>re-inherit</strong> settings from {portfolioName}.
            Both screening and pre-screening settings will be updated to match
            the portfolio, and Vero Go will be{" "}
            <strong>{portfolioVeroGoActive ? "enabled" : "disabled"}</strong> on
            this property.
          </>
        ),
        primaryButtonLabel: `Reset to ${portfolioVeroGoActive ? "On" : "Off"}`,
        secondaryButtonLabel: "Keep custom",
        primaryValue: "confirm",
        secondaryValue: "cancel",
      }).then((result: string) => {
        if (result !== "confirm") return undefined;
        return performReset();
      });
    }

    const veroGoDescription = shouldUseVeroGoReset ? (
      <>
        <strong>Warning:</strong> With Vero Go enabled, resetting screening
        settings here will also reset pre-screening settings to the portfolio.
        {willEnableVeroGoOnReset ? (
          <>
            {" "}
            Vero Go will also be enabled on this property because the portfolio
            is enabled (<strong>On</strong>).
          </>
        ) : null}
        {hasVeroGoMismatch ? (
          <>
            {" "}
            Vero Go status will also be aligned to the portfolio (
            <strong>{portfolioVeroGoActive ? "On" : "Off"}</strong>).
          </>
        ) : null}
      </>
    ) : undefined;

    return openModalDialog(ConfirmationModal, {
      title: "Reset to portfolio settings?",
      subtitle: (
        <span>
          Portfolio: <strong>{portfolioName}</strong>
        </span>
      ),
      submitButtonLabel: "Reset",
      message: (
        <>
          <strong>Please note:</strong> Once you click &quot;Reset&quot; the
          property screening and prescreening settings will be overridden with
          the current portfolio setup and the changes you previously made will
          no longer apply.
        </>
      ),
      description: veroGoDescription,
      onConfirm: performReset,
    });
  };

  return (
    <PropertySettingsFooter
      isPortfolioSettings={isPortfolioSettings}
      openCustomizeSettingsModal={openCustomizeSettingsModal}
      openResetSettingsModal={openResetSettingsModal}
      isActionLoading={isActionLoading}
    />
  );
};

export default PropertySettingsFooterContainer;
